/**
 * Stat cards — list engine v2, "the list is the dashboard".
 *
 * A card is four picks: what to measure, over what time, how to show it, and
 * who sees it. That is deliberately the whole of it. The point of the cards is
 * that anybody can make one without asking for a report to be built, and every
 * pick beyond four is a pick somebody gets stuck on.
 *
 * The definition is in `shared` because three things render from it: the card
 * itself, the builder's live preview, and the filter the list takes when the
 * card is clicked. A card whose preview came from different code than the card
 * would be a preview of something else.
 */
import { z } from 'zod';
import { filterGroup } from './list-query.js';
/** Count rows, or total / average a column. */
export const cardMeasure = z.object({
    fn: z.enum(['count', 'sum', 'avg']),
    /** Required for sum and avg; meaningless for count. */
    field: z.string().min(1).optional(),
});
export const CARD_PERIODS = [
    'all_time',
    'this_week', 'last_7_days', 'last_30_days',
    'month_to_date', 'quarter_to_date', 'year_to_date',
    'last_12_months',
];
export const CARD_PERIOD_LABELS = {
    all_time: 'All time',
    this_week: 'This week',
    last_7_days: 'Last 7 days',
    last_30_days: 'Last 30 days',
    month_to_date: 'Month to date',
    quarter_to_date: 'Quarter to date',
    year_to_date: 'Year to date',
    last_12_months: 'Last 12 months',
};
export const CARD_BUCKETS = ['day', 'week', 'month', 'quarter', 'year'];
export const cardTime = z.object({
    period: z.enum(CARD_PERIODS).default('all_time'),
    /**
     * Which date decides whether a row is in the window.
     *
     * Named explicitly rather than assumed, because "this quarter's revenue"
     * and "this quarter's work" are different questions about the same jobs —
     * one counts by when it was invoiced, the other by when it was finished.
     */
    field: z.string().min(1).optional(),
    /** Trend and Bars need buckets; Number and Target do not. */
    by: z.enum(CARD_BUCKETS).optional(),
});
export const cardCompare = z.union([
    z.object({ kind: z.literal('none') }),
    z.object({ kind: z.literal('previous_period') }),
    z.object({ kind: z.literal('last_year') }),
    z.object({ kind: z.literal('target'), value: z.number() }),
]);
export const CARD_DISPLAYS = ['number', 'trend', 'bars', 'target'];
/** Just me · a branch and below · everyone in the tenant. */
export const CARD_AUDIENCES = ['me', 'scope', 'all'];
/**
 * The colours a card may wear, named in the words a person would use.
 *
 * Each one is a key into the §3.1 palette rather than a colour, for the reason
 * given on `accent` below. Seven, from the job lifecycle, because that row of
 * pairs is already the product's full range of "a colour that means
 * something" — and because a picker with forty swatches is a picker that makes
 * every dashboard look different.
 */
export const CARD_ACCENTS = [
    { value: 'job:completed', label: 'Green' },
    { value: 'job:approved', label: 'Teal' },
    { value: 'job:quoted', label: 'Blue' },
    { value: 'job:invoiced', label: 'Purple' },
    { value: 'job:scheduled', label: 'Amber' },
    { value: 'job:returned', label: 'Red' },
    { value: 'job:requested', label: 'Grey' },
];
/** The CSS colour a stored accent key stands for, or the app's own accent. */
export function accentColor(key) {
    if (!key)
        return 'rgb(var(--color-accent))';
    const [group, status] = key.split(':');
    return `rgb(var(--status-${group}-${status}-fg, var(--color-accent)))`;
}
export const cardDefinition = z.object({
    label: z.string().trim().min(1).max(80),
    /**
     * The colour of the dot beside the label, and of the card's chart.
     *
     * A token key — `job:completed`, `invoice:overdue` — never a colour. The
     * palette in §3.1 is the only source of colour in the product, and a card
     * definition is stored data that would otherwise carry a hex value straight
     * past every guard that exists to stop exactly that.
     */
    accent: z.string().regex(/^[a-z]+:[a-z_]+$/).max(40).optional(),
    /** Which records — the same filter grammar the list itself takes. */
    filter: filterGroup.optional(),
    measure: cardMeasure,
    time: cardTime.default({ period: 'all_time' }),
    compare: cardCompare.default({ kind: 'none' }),
    display: z.enum(CARD_DISPLAYS).default('number'),
});
/** One bucket of a series, or the single value of a Number card. */
export const cardPoint = z.object({
    bucket: z.string(),
    value: z.number(),
});
export const cardResult = z.object({
    value: z.number(),
    series: z.array(cardPoint).optional(),
    /** The same measure over the comparison window, when one was asked for. */
    comparison: z.object({
        value: z.number(),
        series: z.array(cardPoint).optional(),
        /** Null when the comparison window was zero — a change from nothing. */
        changePct: z.number().nullable(),
    }).optional(),
    target: z.number().optional(),
    /** How many records the card counted, for "across 61 invoices". */
    records: z.number(),
});
/**
 * A card with no measurable column is a card that renders `NaN`.
 *
 * Checked here rather than at the four call sites, so the builder, the API and
 * the preview all refuse the same thing for the same reason.
 */
export function cardProblem(definition) {
    if (definition.measure.fn !== 'count' && !definition.measure.field) {
        return 'Choose the column to total.';
    }
    if (definition.display === 'trend' || definition.display === 'bars') {
        if (!definition.time.by)
            return 'A trend needs a period to group by.';
        /*
         * And a date to run along. Without one there is nothing to group by, and
         * the aggregate is refused by the server — so a card built this way looked
         * fine in the builder and came back a 400 with no explanation attached to
         * the pick that caused it.
         */
        if (!definition.time.field)
            return 'Choose which date the trend runs along.';
    }
    if (definition.display === 'target' && definition.compare.kind !== 'target') {
        return 'A target card needs a number to aim at.';
    }
    if (definition.time.period !== 'all_time' && !definition.time.field) {
        return 'Choose which date decides whether a record is in the period.';
    }
    return null;
}
//# sourceMappingURL=list-cards.js.map